import { EditorState, Modifier } from "draft-js";
import React from "react";
import cx from "classnames";

import "./toolbar-field-chooser-button.styles.scss";
import { Button } from "@common/components/Button";

// Global registry to store field data
const fieldRegistry = new Map();
let fieldCounter = 0;

export type ToolbarFieldChooserButtonProps = {
  onChange?: (state: EditorState) => void;
  onClick: (params: { addContent: (text: string) => void }) => void;
  className?: string;
  editorState?: EditorState;
};

export const ToolbarFieldChooserButton = (props: ToolbarFieldChooserButtonProps) => {
  const { editorState, onChange, className, onClick } = props;

  const handleClick = React.useCallback(
    (editorState: EditorState) => {
      onClick({
        addContent: (text: string) => {
          // Parse the HTML to extract span attributes
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = text;
          const spanElement = tempDiv.querySelector('span');
          
          if (spanElement) {
            const spanText = spanElement.textContent || '';
            const fieldId = spanElement.getAttribute('fieldid') || '';
            const systemField = spanElement.getAttribute('systemfield') || '';
            const title = spanElement.getAttribute('title') || '';
            const className = spanElement.getAttribute('class') || '';
            
            // Generate unique field ID and store data in registry
            const uniqueFieldId = `field_${++fieldCounter}_${Date.now()}`;
            const fieldData = {
              fieldId,
              systemField,
              title,
              className,
              text: spanText,
              uniqueFieldId,
            };
            fieldRegistry.set(uniqueFieldId, fieldData);

            // Create a new ContentState with an entity representing this field
            const contentStateWithEntity = editorState
              .getCurrentContent()
              .createEntity('FIELD_PLACEHOLDER', 'IMMUTABLE', fieldData);
            const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

            // Replace the current selection with the visible text linked to the entity
            const contentStateWithText = Modifier.replaceText(
              contentStateWithEntity,
              editorState.getSelection(),
              spanText,
              undefined,
              entityKey,
            );

            // Push the new content state to create an updated EditorState
            let newEditorState = EditorState.push(
              editorState,
              contentStateWithText,
              'insert-characters',
            );

            // Move cursor to the end of the inserted text
            const selection = newEditorState.getSelection();
            const anchorOffset = selection.getAnchorOffset() + spanText.length;
            const newSelection = selection.merge({
              anchorOffset,
              focusOffset: anchorOffset,
            });
            newEditorState = EditorState.forceSelection(newEditorState, newSelection as any);

            if (onChange) {
              onChange(newEditorState);
            }
          }
        },
      });
    },
    [onChange, onClick]
  );

  const execute = React.useCallback(() => {
    if (editorState) {
      handleClick(editorState);
    }
  }, [editorState, handleClick]);

  return (
    <Button
      className={cx("rdw-option-wrapper ToolbarFieldChooserButton", className)}
      onClick={execute}
    >
      {"{+} Field"}
    </Button>
  );
};

// Export the registry for use in HTML conversion
export { fieldRegistry };
